// @ts-nocheck
// 🔒 SECURE GENERAL INVITATION SERVICE - Approval-Based Connection Flow
import { supabase, withConnectionsAccess } from '@/lib/supabaseGuard';
import { baseSupabase } from '@/integrations/supabase/client';
import { notificationService } from '@/services/notifications/notificationService';
import { fetchConnectionRequestsWithSenders, batchFetchUsers } from '@/utils/networkRequestCoordinator';
// V170.3: Import declined request cache helpers
import { isRequestDeclined, addDeclinedRequest, removeDeclinedRequest } from '@/services/declinedRequestCache';
// V177: Import invitation template utilities
import {
  renderInviteMessage,
  buildInviteLink,
  resolveInviteTemplateKey,
  ENGLISH_INVITE_TEMPLATE_KEY,
} from '@/lib/invitations/templates';
import i18n from '@/i18n';
import { normalizeLanguageCode, type LanguageCode } from '@/i18n/languages';

type PublicUserV1 = {
  id: string;
  name: string;
  photo_urls: string[];
  profile_image_public_id: string | null;
  is_verified: boolean;
};

async function getPublicUserV1ById(userId: string): Promise<PublicUserV1 | null> {
  if (!userId) return null;
  const { data, error } = await supabase
    .from('users_public_v1')
    .select('id, display_name, photo_urls, profile_image_public_id, is_verified')
    .eq('id', userId)
    .maybeSingle();
  if (error || !data) return null;
  return {
    id: data.id,
    name: data.display_name || 'Someone',
    photo_urls: Array.isArray(data.photo_urls) ? data.photo_urls : [],
    profile_image_public_id: data.profile_image_public_id ?? null,
    is_verified: data.is_verified === true,
  };
}

async function getPublicUserV1ByIds(userIds: string[]): Promise<PublicUserV1[]> {
  const uniqueIds = [...new Set((userIds || []).filter(Boolean))];
  if (uniqueIds.length === 0) return [];
  const { data, error } = await supabase
    .from('users_public_v1')
    .select('id, display_name, photo_urls, profile_image_public_id, is_verified')
    .in('id', uniqueIds);
  if (error || !data) return [];
  return data.map((row: any) => ({
    id: row.id,
    name: row.display_name || 'Someone',
    photo_urls: Array.isArray(row.photo_urls) ? row.photo_urls : [],
    profile_image_public_id: row.profile_image_public_id ?? null,
    is_verified: row.is_verified === true,
  }));
}

export interface GeneralInvitation {
  id: string;
  creator_id: string;
  invite_code: string;
  template_key: string; // V177: References invitation_templates.key (was: message)
  max_uses: number | null;
  current_uses: number;
  expires_at: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface GeneralInviteProcessResult {
  success: boolean;
  requiresApproval: boolean;
  alreadyConnected: boolean;
  connectionRequestId?: string;
  creatorProfile?: any;
  error?: string;
}

export interface ConnectionRequestNotification {
  id: string;
  sender_id: string;
  recipient_id: string;
  sender_name: string;
  sender_photo?: string;
  sender_email?: string;
  invite_code: string;
  status: 'pending' | 'approved' | 'final_approval_pending' | 'rejected';
  created_at: string;
}

// V284: 2-phase invite acceptance types
export type InvitePreviewDecision =
  | 'NEED_CONSENT'      // Show modal with Accept button
  | 'ALREADY_FRIENDS'   // Show modal with alreadyFriends=true
  | 'REQUEST_PENDING'   // Show toast and close
  | 'INVALID'           // Show error
  | 'BLOCKED';          // Show error

export interface InvitePreviewResult {
  decision: InvitePreviewDecision;
  requester?: {
    id: string;
    name: string;
    photo?: string;
    email?: string;
  };
  error?: string;
  correlationId?: string;
}

export interface InviteConfirmResult {
  success: boolean;
  decision: 'REQUEST_CREATED' | 'ALREADY_FRIENDS' | 'REQUEST_PENDING' | 'ERROR';
  requestId?: string;
  error?: string;
  correlationId?: string;
}

class GeneralInvitationService {
  private readonly inFlightMutations = new Map<string, Promise<any>>();

  private withSingleFlight<T>(key: string, run: () => Promise<T>): Promise<T> {
    const existing = this.inFlightMutations.get(key) as Promise<T> | undefined;
    if (existing) {
      console.log('[InviteFlow] duplicate mutation blocked', { key });
      return existing;
    }

    console.log('[InviteFlow] mutation start', { key });
    const task = run()
      .finally(() => {
        this.inFlightMutations.delete(key);
        console.log('[InviteFlow] mutation complete', { key });
      });

    this.inFlightMutations.set(key, task as Promise<any>);
    return task;
  }

  /**
   * 🔒 V270 SECURITY: Get invitation by invite code using secure RPC
   * Uses validate_invite_code RPC to avoid direct table access
   */
  async getInviteByCode(inviteCode: string): Promise<any> {
    try {
      console.log('🔍 V270 SECURE LOOKUP: Starting invitation validation for:', inviteCode);

      if (!inviteCode || inviteCode.trim() === '') {
        console.error('❌ LOOKUP: Invalid invite code provided');
        return null;
      }

      // V300: Use SECURITY DEFINER RPC for safe invite validation (RLS-compliant)
      console.log('🔍 V300: Validating invite code via RPC:', inviteCode);
      try {
        const { data: validationResult, error: rpcError } = await supabase
          .rpc('validate_invite_code', { p_code: inviteCode });

        if (rpcError) {
          console.error('❌ V300: RPC validation error:', rpcError);
          return null;
        }

        // RPC returns array with single row
        const result = Array.isArray(validationResult) ? validationResult[0] : validationResult;

        if (!result || !result.valid) {
          console.log('❌ V300: Invite validation failed:', result?.error_message || 'Unknown error');
          return null;
        }
        if (!result.creator_id || !result.invite_id) {
          console.log('❌ V300: Invite validation payload missing creator_id or invite_id');
          return null;
        }

        console.log('✅ V300: Invite validated successfully via RPC');

        // Fetch full invitation details using authenticated query (user must be creator)
        // For authenticated users viewing their own invitations
        const { data: { user } } = await supabase.auth.getUser();

        let generalInvite = null;
        if (user && user.id === result.creator_id) {
          // User is the creator, can fetch full details via RLS policy
          const { data, error } = await supabase
            .from('general_invitations')
            .select('*')
            .eq('id', result.invite_id)
            .single();

          if (!error && data) {
            generalInvite = data;
          }
        }

        // Fetch creator details (public profile data)
        console.log('🔍 V300: Fetching creator details for:', result.creator_id);
        const creatorData = await getPublicUserV1ById(result.creator_id);
        console.log('✅ V300: Creator data fetched:', creatorData?.name || 'unreadable_public_profile');

        // V300: Return canonical fields for consistent usage
        return {
          id: result.invite_id,
          invite_code: inviteCode,
          creator_id: result.creator_id,
          creator_name: result.creator_name || creatorData?.name || 'Someone',
          creator_email: null,
          creator_photo_urls: Array.isArray(creatorData?.photo_urls)
            ? creatorData.photo_urls
            : [],
          creator: creatorData,
          is_active: true, // RPC only returns active invites
          current_uses: generalInvite?.current_uses || 0,
          max_uses: generalInvite?.max_uses || null,
          expires_at: generalInvite?.expires_at || null,
          template_key: generalInvite?.template_key || 'whatsapp_default',
          message: generalInvite?.template_key || '',
          isLegacy: false
        };
      } catch (rpcException) {
        console.error('❌ V300: Exception during RPC validation:', rpcException);
        return null;
      }

      // METHOD 2: Check connection_requests table (legacy system)
      console.log('🔍 LOOKUP: Checking connection_requests table (legacy)');
      console.warn('⚠️ V163.4: lookupInvitation() - Use NetworkDataContext for connection requests');
      try {
        // V163.4: Connection requests now loaded via NetworkDataContext
        // Components should use useConnectionRequests() hook instead
        const legacyInvite = null; // Neutralized - use context hook
        const legacyError = null;

      if (!legacyError && legacyInvite) {
        console.log('✅ LOOKUP: Found in connection_requests table:', legacyInvite);

        // Get creator details
        const creatorData = await getPublicUserV1ById((legacyInvite as any).sender_id);

        return {
          id: (legacyInvite as any).id,
          invite_code: (legacyInvite as any).invite_code,
          creator_id: (legacyInvite as any).sender_id,
          creator: creatorData,
          creator_name: creatorData?.name || 'Someone',
          message: 'Join me on BlissNet - a new social dating app!',
          max_uses: null,
          current_uses: 1,
          is_active: (legacyInvite as any).status !== 'declined',
          created_at: (legacyInvite as any).created_at,
          isLegacy: true
        };
      }

      console.log('🔍 LOOKUP: Not found in connection_requests');
      return null;
    } catch (legacyException) {
      console.log('🔍 LOOKUP: Exception in connection_requests lookup:', legacyException);
      return null;
    }
    // FIX: Close the outer try block (from line 77) and the function
  } catch (outerError) {
    console.error('❌ Error in getInviteByCode:', outerError);
    return null;
  }
  }

  /**
   * Get legacy invite from connection_requests table
   * Handles backward compatibility with old invite system
   */
  async getLegacyInvite(inviteCode: string): Promise<any> {
    try {
      console.log('🔄 Checking legacy invite system for code:', inviteCode);

      // V163.4: Connection requests now loaded via NetworkDataContext
      console.warn('⚠️ V163.4: checkLegacyInvite() - Use NetworkDataContext for connection requests');
      const data = null; // Neutralized - use useConnectionRequests() hook
      const error = null;

      if (error) {
        console.error('❌ Error checking legacy invites:', error);
        return null;
      }

      if (!data) {
        console.log('❌ No legacy invite found');
        return null;
      }

      // Convert legacy format to general invite format
      const legacyInvite = {
        id: data.id,
        creator_id: data.sender_id,
        invite_code: inviteCode,
        message: 'Join me on BlissNet - a new social dating app!',
        max_uses: null,
        current_uses: 1,
        expires_at: null,
        is_active: data.status === 'PENDING',
        created_at: data.created_at,
        updated_at: data.updated_at || data.created_at,
        creator: data.sender,
        isLegacy: true // Mark as legacy for special handling
      };

      console.log('✅ Legacy invite found and converted:', legacyInvite);
      return legacyInvite;
    } catch (error) {
      console.error('💥 Failed to get legacy invite:', error);
      return null;
    }
  }

  /**
   * 🔒 SECURE: Process general invitation with approval flow
   * NO AUTO-CONNECTIONS - Creates pending connection request instead
   */
  async processGeneralInvitation(inviteCode: string, userId: string): Promise<GeneralInviteProcessResult> {
    try {
      console.log('🔒 V300: Processing general invitation with approval flow', { inviteCode, userId });

      // V300: Step 1: Validate invitation via RPC (RLS-compliant)
      const { data: validationResult, error: rpcError } = await supabase
        .rpc('validate_invite_code', { p_code: inviteCode });

      if (rpcError) {
        console.error('❌ V300: RPC validation error:', rpcError);
        return { success: false, requiresApproval: false, alreadyConnected: false, error: 'Database error' };
      }

      const result = Array.isArray(validationResult) ? validationResult[0] : validationResult;

      if (!result || !result.valid) {
        console.log('❌ V300: Invitation validation failed:', result?.error_message);
        return { success: false, requiresApproval: false, alreadyConnected: false, error: result?.error_message || 'Invalid or expired invitation' };
      }
      if (!result.creator_id || !result.invite_id) {
        return { success: false, requiresApproval: false, alreadyConnected: false, error: 'Invalid invitation payload' };
      }

      // Build invitation object from RPC result
      const invitation = {
        id: result.invite_id,
        invite_code: inviteCode,
        creator_id: result.creator_id,
        is_active: true
      };

    
   // Step 2: Validate invitation constraints
      const constraintsResult = await this.validateInvitation(invitation); // ✅ Renamed
      if (!constraintsResult.valid) {
        return { success: false, requiresApproval: false, alreadyConnected: false, error: constraintsResult.error };
      }

      // Step 3: Get creator profile (non-fatal — invite can proceed with placeholder if RLS blocks)
      const creatorProfile = await getPublicUserV1ById(invitation.creator_id);

      // V283.7: Step 4 - Prevent self-invitation (CRITICAL GUARD)
      console.log('🔍 V283.7: Self-invite check:', {
        currentUserId: userId,
        creatorId: invitation.creator_id,
        areEqual: userId === invitation.creator_id,
        inviteCode: inviteCode
      });

      if (userId === invitation.creator_id) {
        console.error('❌ V283.7: SELF-INVITE BLOCKED - User opened their own invite link!', {
          userId,
          creatorId: invitation.creator_id,
          inviteCode
        });
        return {
          success: false,
          requiresApproval: false,
          alreadyConnected: false,
          error: 'This is your own invite link. Share it with others to connect!'
        };
      }

      console.log('✅ V283.7: Self-invite check passed - different users');


      // Step 5: Check if already connected (comprehensive check)
      const connectionCheck = await this.checkExistingConnectionComprehensive(userId, invitation.creator_id);
      if (connectionCheck.exists) {
        console.log('✅ Users already connected', connectionCheck);
        return {
          success: true,
          requiresApproval: false,
          alreadyConnected: true,
          creatorProfile
        };
      }

      // Step 6: Check for existing pending request (this is now redundant due to comprehensive check above)
      // But keeping for extra safety and specific error messaging
      const existingRequest = await this.checkExistingConnectionRequest(userId, invitation.creator_id);
      if (existingRequest.exists) {
        console.log('⏳ Connection request already pending (secondary check)');
        return {
          success: true,
          requiresApproval: true,
          alreadyConnected: false,
          connectionRequestId: existingRequest.requestId,
          creatorProfile,
          error: 'Connection request already sent and pending approval'
        };
      }

      // V283.8: FIXED - Create connection request from INVITEE to INVITER
      // When user accepts invite, INVITEE sends request TO INVITER for approval
      // INVITER will see the request in their Requests tab and can approve/decline
      console.log('🔧 V283.8: Creating connection request with CORRECT mapping:', {
        senderId: userId,
        senderRole: 'INVITEE (currentUser who clicked invite link)',
        recipientId: invitation.creator_id,
        recipientRole: 'INVITER (creator who will approve)',
        inviteCode: inviteCode,
        flow: 'Invitee sends request TO inviter (inviter will approve)'
      });

      // V283.8: Direct call to createConnectionRequest with correct direction
      const requestResult = await this.createConnectionRequest(
        userId,                 // V283.8: INVITEE (currentUser) is the sender
        invitation.creator_id,  // V283.8: INVITER (creator) is the recipient who will approve
        inviteCode
      );

      if (!requestResult.success) {
        console.error('❌ V283.8: Failed to create connection request:', requestResult.error);
        return { success: false, requiresApproval: false, alreadyConnected: false, error: requestResult.error };
      }

      console.log('✅ V283.8: Connection request created - inviter will see request from invitee');

      // Step 8: Update invitation usage
      await this.incrementInvitationUsage(invitation.id);

      // V283.8: Step 9 - Send notification to INVITER (recipient of request)
      // Notify inviter that invitee sent them a connection request
      console.log('🔧 V283.8: Notifying inviter about connection request from invitee');
      await this.notifyCreatorOfConnectionRequest(
        invitation.creator_id,  // V283.8: Notify the INVITER (recipient of request)
        userId,                 // V283.8: About the INVITEE (sender of request)
        inviteCode
      );

      console.log('✅ V283.8: General invitation processed - connection request sent to inviter for approval');
      return {
        success: true,
        requiresApproval: true,
        alreadyConnected: false,
        creatorProfile
      };

    } catch (error) {
      console.error('💥 Error processing general invitation:', error);
      return { success: false, requiresApproval: false, alreadyConnected: false, error: 'Unexpected error occurred' };
    }
  }

  /**
   * Authoritative preview relationship resolution (read-only).
   * Returns ALREADY_FRIENDS / REQUEST_PENDING, or null when neither exists.
   * Query failures are rethrown — they must never be treated as proven INVALID.
   */
  private async resolvePreviewRelationshipState(
    userId: string,
    creatorId: string,
    correlationId: string
  ): Promise<'ALREADY_FRIENDS' | 'REQUEST_PENDING' | null> {
    let connectionRows: Array<{ id: string; status: string | null }> | null = null;
    try {
      const { data, error } = await withConnectionsAccess(() =>
        supabase
          .from('connections')
          .select('id, status')
          .or(
            `and(user_id.eq.${userId},connection_user_id.eq.${creatorId}),and(user_id.eq.${creatorId},connection_user_id.eq.${userId})`
          )
          .limit(10)
      );
      if (error) {
        console.warn(`⚠️ PREVIEW [${correlationId}]: connection_query_failed`);
        throw new Error('connection_query_failed');
      }
      connectionRows = data;
    } catch (connError) {
      console.warn(`⚠️ PREVIEW [${correlationId}]: connection_query_failed`);
      throw connError;
    }

    // Case-insensitive ACCEPTED only — aligned with RPC upper(status)='ACCEPTED'.
    const existingConnection = (connectionRows || []).find(
      (row) => String(row.status || '').toUpperCase() === 'ACCEPTED'
    );

    if (existingConnection) {
      console.log(`✅ V284.1 PREVIEW [${correlationId}]: Already friends`);
      return 'ALREADY_FRIENDS';
    }

    try {
      const { data: requestRows, error: pendingError } = await supabase
        .from('connection_requests')
        .select('id, status')
        .or(
          `and(sender_id.eq.${userId},recipient_id.eq.${creatorId}),and(sender_id.eq.${creatorId},recipient_id.eq.${userId})`
        )
        .limit(10);

      if (pendingError) {
        console.warn(`⚠️ PREVIEW [${correlationId}]: pending_query_failed`);
        throw new Error('pending_query_failed');
      }

      // Case-insensitive pending — aligned with RPC lower(status)='pending'.
      const pendingRequest = (requestRows || []).find(
        (row) => String(row.status || '').toLowerCase() === 'pending'
      );

      if (pendingRequest) {
        console.log(`⏳ V284.1 PREVIEW [${correlationId}]: Request already pending`);
        return 'REQUEST_PENDING';
      }
    } catch (pendingErr) {
      console.warn(`⚠️ PREVIEW [${correlationId}]: pending_query_failed`);
      throw pendingErr;
    }

    return null;
  }

  /**
   * V284.1: PREVIEW PHASE - Validate invite and compute relationship (NO DB WRITES)
   * Returns UI decision without creating any database records
   */
  async previewGeneralInvitation(inviteCode: string, userId: string): Promise<InvitePreviewResult> {
    const correlationId = `${inviteCode.substring(0, 8)}-${userId.substring(0, 8)}-${Date.now()}`;

    try {
      console.log(`🔍 V300 PREVIEW START [${correlationId}]`);

      // V300: Step 1: Validate invitation via RPC (RLS-compliant)
      const { data: validationResult, error: rpcError } = await supabase
        .rpc('validate_invite_code', { p_code: inviteCode });

      if (rpcError) {
        console.warn(`⚠️ PREVIEW [${correlationId}]: validate_rpc_failed`);
        throw rpcError instanceof Error ? rpcError : new Error('validate_rpc_failed');
      }

      const result = Array.isArray(validationResult) ? validationResult[0] : validationResult;

      // Soft-invalid: invite not usable, but creator_id may still allow relationship resolution.
      if (!result || !result.valid) {
        if (!result?.creator_id) {
          console.log(`❌ V300 PREVIEW [${correlationId}]: Invite validation failed`);
          return {
            decision: 'INVALID',
            error: result?.error_message || 'Invalid or expired invitation',
            correlationId,
          };
        }

        const softState = await this.resolvePreviewRelationshipState(
          userId,
          result.creator_id,
          correlationId
        );
        if (softState === 'ALREADY_FRIENDS' || softState === 'REQUEST_PENDING') {
          const inviterProfile = await getPublicUserV1ById(result.creator_id);
          return {
            decision: softState,
            requester: {
              id: result.creator_id,
              name: inviterProfile?.name || 'Someone',
              photo: inviterProfile?.photo_urls?.[0],
              email: undefined,
            },
            correlationId,
          };
        }

        console.log(`❌ V300 PREVIEW [${correlationId}]: Soft-invalid with no relationship`);
        return {
          decision: 'INVALID',
          error: result?.error_message || 'Invalid or expired invitation',
          correlationId,
        };
      }

      if (!result.creator_id || !result.invite_id) {
        return { decision: 'INVALID', error: 'Invalid invitation payload', correlationId };
      }

      // Build invitation object from RPC result
      const invitation = {
        id: result.invite_id,
        invite_code: inviteCode,
        creator_id: result.creator_id,
        is_active: true
      };

      // Step 2: Self-invite guard
      if (userId === invitation.creator_id) {
        console.log(`❌ V284.1 PREVIEW [${correlationId}]: Self-invite blocked`);
        return {
          decision: 'INVALID',
          error: 'This is your own invite link. Share it with others to connect!',
          correlationId
        };
      }

      // Step 3: Validate invitation (expiry, usage limits)
      const validation = await this.validateInvitation(invitation);
      if (!validation.valid) {
        console.log(`❌ V284.1 PREVIEW [${correlationId}]: Validation failed`);
        return { decision: 'INVALID', error: validation.error, correlationId };
      }

      // Step 4: Get inviter profile (non-fatal — use placeholder if RLS blocks)
      const inviterProfile = await getPublicUserV1ById(invitation.creator_id);

      const requester = {
        id: invitation.creator_id,
        name: inviterProfile?.name || 'Someone',
        photo: inviterProfile?.photo_urls?.[0],
        email: undefined
      };

      // Steps 5–6: Authoritative relationship checks
      const relationshipState = await this.resolvePreviewRelationshipState(
        userId,
        invitation.creator_id,
        correlationId
      );
      if (relationshipState === 'ALREADY_FRIENDS' || relationshipState === 'REQUEST_PENDING') {
        return { decision: relationshipState, requester, correlationId };
      }

      // Step 7: All clear - need user consent
      console.log(`✅ V284.1 PREVIEW END [${correlationId}]: NEED_CONSENT`);
      return { decision: 'NEED_CONSENT', requester, correlationId };

    } catch (error) {
      console.error(`💥 V284.1 PREVIEW ERROR [${correlationId}]: preview_unexpected`);
      throw error;
    }
  }

  /**
   * V284.2: CONFIRM PHASE - Create connection request after user consent (WITH DB WRITES)
   * Race-safe: re-checks relationship state before writing
   */
  async confirmGeneralInvitation(inviteCode: string, userId: string): Promise<InviteConfirmResult> {
    const correlationId = `${inviteCode.substring(0, 8)}-${userId.substring(0, 8)}-${Date.now()}`;
    const mutationKey = `confirm:${inviteCode}:${userId}`;

    return this.withSingleFlight(mutationKey, async () => {
      try {
        console.log('[InviteFlow] confirm mutation begin', { correlationId, mutationKey });
        const { data, error } = await supabase.rpc('confirm_general_invitation_canonical', {
          p_invite_code: inviteCode,
          p_user_id: userId,
          p_idempotency_key: mutationKey,
        });

        if (error) {
          console.error('[InviteFlow] confirm mutation error', { correlationId, mutationKey, error });
          return { success: false, decision: 'ERROR', error: 'Failed to process invitation', correlationId };
        }

        const rpcRow = Array.isArray(data) ? data[0] : data;
        if (!rpcRow?.success) {
          console.warn('[InviteFlow] confirm mutation failed', { correlationId, mutationKey, rpcRow });
          return {
            success: false,
            decision: 'ERROR',
            error: rpcRow?.error || 'Failed to process invitation',
            correlationId: rpcRow?.correlation_id || correlationId,
          };
        }

        const decision = rpcRow.decision as InviteConfirmResult['decision'];
        const response: InviteConfirmResult = {
          success: true,
          decision: decision || 'REQUEST_PENDING',
          requestId: rpcRow.request_id || undefined,
          correlationId: rpcRow.correlation_id || correlationId,
        };

        if (response.decision === 'REQUEST_CREATED') {
          try {
            const { data: inviteValidation } = await supabase.rpc('validate_invite_code', { p_code: inviteCode });
            const inviteResult = Array.isArray(inviteValidation) ? inviteValidation[0] : inviteValidation;
            if (inviteResult?.creator_id) {
              await this.notifyCreatorOfConnectionRequest(inviteResult.creator_id, userId, inviteCode);
            }
          } catch (notifyError) {
            console.warn('[InviteFlow] notification dispatch failed (non-fatal)', { correlationId, notifyError });
          }
        }

        console.log('[InviteFlow] confirm mutation success', { correlationId, mutationKey, decision: response.decision });
        return response;
      } catch (error) {
        console.error('[InviteFlow] confirm mutation terminal error', { correlationId, mutationKey, error });
        return { success: false, decision: 'ERROR', error: 'Unexpected error occurred', correlationId };
      }
    });
  }

  /**
   * 🔒 SECURITY: Comprehensive invitation validation
   */
  private async validateInvitation(invitation: GeneralInvitation): Promise<{ valid: boolean; error?: string }> {
    // Security Check 1: Invitation must be active
    if (!invitation.is_active) {
      console.log('❌ SECURITY: Invitation is deactivated');
      return { valid: false, error: 'This invitation has been deactivated' };
    }

    // Security Check 2: Check expiry
    if (invitation.expires_at) {
      const expiryDate = new Date(invitation.expires_at);
      const now = new Date();
      if (expiryDate < now) {
        console.log('❌ SECURITY: Invitation expired', { expiryDate, now });
        return { valid: false, error: 'Invitation has expired' };
      }
    }

    // Security Check 3: Check usage limits
    if (invitation.max_uses !== null && invitation.current_uses >= invitation.max_uses) {
      console.log('❌ SECURITY: Invitation usage limit exceeded', {
        maxUses: invitation.max_uses,
        currentUses: invitation.current_uses
      });
      return { valid: false, error: 'Invitation has reached maximum usage limit' };
    }

    // Security Check 4: Strict creator_id integrity (profile readability is NOT an integrity gate)
    if (!invitation.creator_id || typeof invitation.creator_id !== 'string') {
      console.log('❌ SECURITY: Invalid creator_id on invitation record');
      return { valid: false, error: 'Invalid invitation creator' };
    }

    // Security Check 5: Rate limiting - check if too many requests from same IP/user recently
    // This would require additional tracking, for now we'll log it
    console.log('✅ SECURITY: Invitation validation passed', {
      inviteCode: invitation.invite_code,
      creatorId: invitation.creator_id,
      currentUses: invitation.current_uses,
      maxUses: invitation.max_uses
    });

    return { valid: true };
  }

  /**
   * 🔧 COMPREHENSIVE: Check if users are already connected or have pending requests
   */
  private async checkExistingConnectionComprehensive(userId1: string, userId2: string): Promise<{
    exists: boolean;
    connectionId?: string;
    connectionType?: 'connection' | 'pending_request';
    details?: any;
  }> {
    try {
      console.log('🔍 COMPREHENSIVE CHECK: Checking existing connections and requests', { userId1, userId2 });

      // ✅ VALIDATION: Prevent self-connections at the database level
      if (userId1 === userId2) {
        console.error('❌ VALIDATION: Self-connection attempt blocked in comprehensive check');
        return {
          exists: true,
          connectionType: 'connection',
          details: { error: 'Cannot connect to yourself', type: 'self_connection' }
        };
      }

      // Check 1: Existing connections in connections table
      // V167.FIX: This query is blocked by V162 Supabase Guard
      // The guard prevents direct connection queries to maintain optimization
      // We'll catch the error and continue - the actual connection creation still works
      let connections = null;
      let connectionError = null;

      try {
        const result = await supabase
          .from('connections')
          .select('id, status, type')
          .or(`and(user_id.eq.${userId1},connection_user_id.eq.${userId2}),and(user_id.eq.${userId2},connection_user_id.eq.${userId1})`)
          .not('user_id', 'is', null)  // ✅ FIX: Exclude null UUIDs
          .not('connection_user_id', 'is', null);  // ✅ FIX: Exclude null UUIDs

        connections = result.data;
        connectionError = result.error;
      } catch (guardError: any) {
        // V167.FIX: Supabase Guard blocks this query - that's OK, continue anyway
        if (guardError?.message?.includes('Direct connection queries are disabled')) {
          console.log('⚠️ V167.FIX: Connection query blocked by V162 guard - continuing without check');
          // Return false to allow the flow to continue
          // The actual connection creation will handle duplicates
          return { exists: false };
        }
        throw guardError; // Re-throw if it's a different error
      }

      if (connectionError) {
        console.error('❌ Error checking connections:', connectionError);
      }

      if (connections && connections.length > 0) {
        console.log('🔍 FOUND: Existing connection(s)', connections);

        // V175.BLOCK: Check for BLOCKED connections first (highest priority)
        const blockedConnection = connections.find(conn =>
          conn.status === 'BLOCKED'
        );

        if (blockedConnection) {
          console.log('🚫 V175.BLOCK: Connection is blocked - cannot send invite', blockedConnection);
          return {
            exists: true,
            connectionId: blockedConnection.id,
            connectionType: 'blocked',
            details: blockedConnection
          };
        }

        // Check for active/accepted connections (these should block invite flow)
        const activeConnection = connections.find(conn =>
          conn.status === 'accepted' ||
          conn.status === 'ACCEPTED' ||
          conn.status === 'active' ||
          conn.status === 'ACTIVE' ||
          (conn.status === 'PENDING' && conn.type === 'friend') // Pending friend requests should block
        );

        if (activeConnection) {
          console.log('✅ FOUND: Active connection that blocks invite flow', activeConnection);
          return {
            exists: true,
            connectionId: activeConnection.id,
            connectionType: 'connection',
            details: activeConnection
          };
        }

        // Check for pending dating connections (these should NOT block invite flow)
        const pendingDatingConnection = connections.find(conn =>
          conn.status === 'PENDING' && (conn.type === 'like' || conn.type === 'match')
        );

        if (pendingDatingConnection) {
          console.log('⚠️ FOUND: Pending dating connection - should NOT block invite flow', pendingDatingConnection);
          // Don't return exists=true, let the invite flow continue
        }
      }

      // Check 2: Existing connection requests (any status)
      // V163.4: Connection requests now loaded via NetworkDataContext
      console.warn('⚠️ V163.4: checkExistingConnection() - Use NetworkDataContext for connection requests');
      const requests = []; // Neutralized - use useConnectionRequests() hook
      const requestError = null;

      if (requestError) {
        console.error('❌ Error checking connection requests:', requestError);
      }

      if (requests && requests.length > 0) {
        const activeRequest = requests.find(req => req.status === 'PENDING') || requests[0];
        console.log('✅ FOUND: Existing connection request(s)', { count: requests.length, activeRequest });

        // If there's an accepted request, they should be connected
        const acceptedRequest = requests.find(req => req.status === 'accepted');
        if (acceptedRequest) {
          console.log('⚠️ INCONSISTENCY: Accepted request but no connection found', acceptedRequest);
          return {
            exists: true,
            connectionId: acceptedRequest.id,
            connectionType: 'pending_request',
            details: { ...acceptedRequest, inconsistency: 'accepted_request_no_connection' }
          };
        }

        // If there's a pending request, DON'T treat as connected - just track it
        const pendingRequest = requests.find(req => req.status === 'PENDING');
        if (pendingRequest) {
          console.log('⏳ FOUND: Pending connection request - NOT blocking invite flow', pendingRequest);
          // Don't return exists=true for pending requests - they're not connected yet!
          // The invite flow should handle this as "request already pending"
        }
      }

      console.log('✅ NO EXISTING: No connections or requests found');
      return { exists: false };

    } catch (error) {
      console.error('💥 Error in comprehensive connection check:', error);
      // In case of error, assume no connection to allow processing
      return { exists: false };
    }
  }

  /**
   * 🛡️ DUPLICATE PREVENTION: Comprehensive check to prevent duplicate connections
   */
  private async preventDuplicateConnection(userId1: string, userId2: string): Promise<{ canCreate: boolean; reason?: string }> {
    try {
      // Check 1: Self-connection prevention
      if (userId1 === userId2) {
        return { canCreate: false, reason: 'Cannot create connection to yourself' };
      }

      // Check 2: Existing connections (both directions with lexical ordering)
      const [smallerId, largerId] = [userId1, userId2].sort();
      const { data: existingConnections, error: connError } = await supabase
        .from('connections')
        .select('id, status, type')
        .eq('user_id', smallerId)
        .eq('connection_user_id', largerId)
        .not('user_id', 'is', null)
        .not('connection_user_id', 'is', null);

      if (connError) {
        console.error('❌ DUPLICATE CHECK: Error checking existing connections:', connError);
        return { canCreate: false, reason: 'Database error during duplicate check' };
      }

      if (existingConnections && existingConnections.length > 0) {
        const activeConnection = existingConnections.find(conn =>
          conn.status === 'ACCEPTED' || conn.status === 'ACTIVE'
        );
        if (activeConnection) {
          return { canCreate: false, reason: 'Active connection already exists between these users' };
        }
      }

      // Check 3: Pending connection requests (both directions)
      // V163.4: Connection requests now loaded via NetworkDataContext
      console.warn('⚠️ V163.4: canCreateConnectionRequest() - Use NetworkDataContext for connection requests');
      const pendingRequests = []; // Neutralized - use useConnectionRequests() hook
      const reqError = null;

      if (reqError) {
        console.error('❌ DUPLICATE CHECK: Error checking pending requests:', reqError);
        return { canCreate: false, reason: 'Database error during request check' };
      }

      if (pendingRequests && pendingRequests.length > 0) {
        return { canCreate: false, reason: 'Pending connection request already exists between these users' };
      }

      return { canCreate: true };

    } catch (error) {
      console.error('💥 DUPLICATE CHECK: Exception during validation:', error);
      return { canCreate: false, reason: 'Unexpected error during duplicate check' };
    }
  }

  /**
   * 🔒 SECURITY: Check for existing pending connection request with comprehensive validation
   */
  private async checkExistingConnectionRequest(userId: string, creatorId: string): Promise<{ exists: boolean; requestId?: string; status?: string; error?: string }> {
    try {
      // Check for any existing requests between these users (both directions)
      // V163.4: Connection requests now loaded via NetworkDataContext
      console.warn('⚠️ V163.4: checkExistingConnectionRequest() - Use NetworkDataContext for connection requests');
      const requests = []; // Neutralized - use useConnectionRequests() hook
      const error = null;

      if (error) {
        console.error('❌ SECURITY: Error checking existing requests:', error);
        return { exists: false, error: 'Database error checking existing requests' };
      }

      if (requests && requests.length > 0) {
        const pendingRequest = requests.find(req => req.status === 'PENDING');
        const acceptedRequest = requests.find(req => req.status === 'ACCEPTED');

        if (acceptedRequest) {
          console.log('✅ SECURITY: Connection request already accepted (users are connected)', acceptedRequest);
          return { exists: true, requestId: acceptedRequest.id, status: 'ACCEPTED' };
        }

        if (pendingRequest) {
          console.log('⏳ SECURITY: Connection request already pending approval', pendingRequest);
          return { exists: true, requestId: pendingRequest.id, status: 'PENDING' };
        }
      }

      return { exists: false };

    } catch (error) {
      console.error('💥 SECURITY: Unexpected error checking requests:', error);
      return { exists: false, error: 'Unexpected error occurred' };
    }
  }

  /**
   * 🚀 V167.INVITE.FIX: Create friend request instead of direct connection
   * This respects V162 NetworkDataContext architecture and avoids Supabase Guard violations
   * V170.3: Added cache check to prevent duplicate key errors
   * V175.BLOCK: Added BLOCKED status check
   */
  private async createDirectConnection(userId1: string, userId2: string, inviteCode?: string): Promise<{ success: boolean; connectionId?: string; declined?: boolean; declinedAt?: string; blocked?: boolean; error?: string }> {
    try {
      console.log('🚀 V170.3: createDirectConnection called', { userId1, userId2, inviteCode });

      // V175.BLOCK: Check for BLOCKED connections FIRST (highest priority)
      try {
        const { data: blockedConnections, error: blockedCheckError } = await withConnectionsAccess(() =>
          supabase
            .from('connections')
            .select('id, status')
            .or(`and(user_id.eq.${userId1},connection_user_id.eq.${userId2}),and(user_id.eq.${userId2},connection_user_id.eq.${userId1})`)
            .eq('status', 'BLOCKED')
            .limit(1)
        );

        if (blockedCheckError) {
          console.warn('⚠️ V175.BLOCK: Error checking blocked connections (non-critical):', blockedCheckError);
        } else if (blockedConnections && blockedConnections.length > 0) {
          console.log('🚫 V175.BLOCK: Connection is blocked - cannot send request');
          return {
            success: false,
            blocked: true,
            error: 'Connection is blocked'
          };
        }
      } catch (guardError: any) {
        // V167.FIX: Supabase Guard might block this query - that's OK, continue anyway
        if (guardError?.message?.includes('Direct connection queries are disabled')) {
          console.log('⚠️ V175.BLOCK: Connection query blocked by guard - continuing without check');
        } else {
          console.warn('⚠️ V175.BLOCK: Error in blocked check (non-critical):', guardError);
        }
      }

      // V170.7: CRITICAL FIX - Check connections table for ACCEPTED (source of truth)
      // This prevents false "already friends" errors from stale connection_requests
      try {
        const { data: existingConnections, error: connectionCheckError } = await withConnectionsAccess(() =>
          supabase
            .from('connections')
            .select('id, status')
            .or(`and(user_id.eq.${userId1},connection_user_id.eq.${userId2}),and(user_id.eq.${userId2},connection_user_id.eq.${userId1})`)
            .eq('status', 'ACCEPTED')
            .limit(1)
        );

        if (connectionCheckError) {
          console.warn('⚠️ V170.7: Error checking existing connections (non-critical):', connectionCheckError);
        } else if (existingConnections && existingConnections.length > 0) {
          console.log('⚠️ V170.7: Users are already connected in connections table');
          return {
            success: false,
            error: 'You are already friends with this user'
          };
        }
      } catch (guardError: any) {
        // V167.FIX: Supabase Guard might block this query - that's OK, continue anyway
        if (guardError?.message?.includes('Direct connection queries are disabled')) {
          console.log('⚠️ V170.7: Connection query blocked by guard - continuing without check');
        } else {
          console.warn('⚠️ V170.7: Error in connection check (non-critical):', guardError);
        }
      }

      // V170.3: Step 1 - Check cache FIRST (instant check, no server query)
      console.log('🚀 V170.3: Step 1 - Checking declined request cache...');
      const cachedDeclined = isRequestDeclined(userId1, userId2);
      if (cachedDeclined) {
        console.log('✅ V170.3: Found declined request in cache (instant check)');
        console.log('🚀 V170.3: Blocking request send - user already declined');
        return {
          success: false,
          declined: true,
          declinedAt: cachedDeclined.declinedAt,
          error: 'Request was previously declined'
        };
      }
      console.log('✅ V170.3: No declined request in cache, proceeding...');

      // V170.3: Step 2 - Check database for existing request BEFORE INSERT
      console.log('🚀 V170.3: Step 2 - Checking database for existing request...');
      const { data: existingRequest, error: checkError } = await supabase
        .from('connection_requests')
        .select('id, status, declined_at')
        .or(`and(sender_id.eq.${userId1},recipient_id.eq.${userId2}),and(sender_id.eq.${userId2},recipient_id.eq.${userId1})`)
        .limit(1)
        .maybeSingle();

      if (checkError) {
        console.error('❌ V170.3: Error checking existing request:', checkError);
        // Continue with insert attempt if check fails (fallback behavior)
      } else if (existingRequest) {
        console.log('📋 V170.3: Found existing request:', existingRequest.status);

        // V170.3: Handle declined status
        if (existingRequest.status === 'declined' || existingRequest.status === 'rejected') {
          console.log('✅ V170.3: Request was declined in database, adding to cache');
          // Add to cache for future instant checks
          addDeclinedRequest(
            userId1,
            userId2,
            undefined, // recipientName not available here
            existingRequest.declined_at || new Date().toISOString()
          );
          return {
            success: false,
            declined: true,
            declinedAt: existingRequest.declined_at || new Date().toISOString(),
            error: 'Request was previously declined'
          };
        }

        // V170.3: Handle pending status
        if (existingRequest.status === 'pending' || existingRequest.status === 'PENDING') {
          console.log('⚠️ V170.3: Request already pending');
          return {
            success: false,
            error: 'Friend request already sent and pending approval'
          };
        }

        // V170.7: Handle accepted/approved status - verify connection exists first
        if (existingRequest.status === 'accepted' || existingRequest.status === 'approved' || existingRequest.status === 'ACCEPTED') {
          // Double-check if connection actually exists (might be stale request)
          try {
            const { data: verifyConnections } = await withConnectionsAccess(() =>
              supabase
                .from('connections')
                .select('id')
                .or(`and(user_id.eq.${userId1},connection_user_id.eq.${userId2}),and(user_id.eq.${userId2},connection_user_id.eq.${userId1})`)
                .eq('status', 'ACCEPTED')
                .limit(1)
            );

            if (verifyConnections && verifyConnections.length > 0) {
              console.log('✅ V170.7: Users already connected (verified)');
              return {
                success: false,
                error: 'You are already friends with this user'
              };
            } else {
              // Stale request - clean it up and allow new request
              console.log('🧹 V170.7: Found stale accepted request but no connection exists, cleaning up');
              await supabase
                .from('connection_requests')
                .delete()
                .eq('id', existingRequest.id);
              // Continue to create new request below
            }
          } catch (verifyError: any) {
            // If verification fails, assume stale and clean up
            if (verifyError?.message?.includes('Direct connection queries are disabled')) {
              console.log('⚠️ V170.7: Connection verification blocked by guard, cleaning up stale request');
            } else {
              console.warn('⚠️ V170.7: Error verifying connection (non-critical):', verifyError);
            }
            // Clean up stale request
            await supabase
              .from('connection_requests')
              .delete()
              .eq('id', existingRequest.id);
            // Continue to create new request below
          }
        }
      }

      // V170.3: Step 3 - Only INSERT if no existing request found
      console.log('🚀 V170.3: Step 3 - No existing request found, creating new request...');
      console.log('🚀 V167.INVITE.FIX: Creating friend request (not direct connection)');
      const requestId = crypto.randomUUID();

      const { error: requestError } = await supabase
        .from('connection_requests')
        .insert({
          id: requestId,
          sender_id: userId1,
          recipient_id: userId2,
          invite_code: inviteCode || null, // ✅ V167.INVITE.FIX: Include invite code!
          status: 'pending',
          created_at: new Date().toISOString()
        });

      if (requestError) {
        console.error('❌ V170.3: Error creating friend request:', requestError);
        
        // V170.3: Handle duplicate key error gracefully
        if (requestError.code === '23505' || requestError.message?.includes('duplicate key')) {
          console.log('⚠️ V170.3: Duplicate key error detected, checking if declined...');
          // Re-check database to see if it's declined
          const { data: recheckRequest } = await supabase
            .from('connection_requests')
            .select('id, status, declined_at')
            .or(`and(sender_id.eq.${userId1},recipient_id.eq.${userId2}),and(sender_id.eq.${userId2},recipient_id.eq.${userId1})`)
            .limit(1)
            .maybeSingle();

          if (recheckRequest?.status === 'declined') {
            console.log('✅ V170.3: Request is declined, adding to cache');
            addDeclinedRequest(
              userId1,
              userId2,
              undefined,
              recheckRequest.declined_at || new Date().toISOString()
            );
            return {
              success: false,
              declined: true,
              declinedAt: recheckRequest.declined_at || new Date().toISOString(),
              error: 'Request was previously declined'
            };
          }
        }

        return { success: false, error: 'Failed to send friend request' };
      }

      console.log('✅ V170.3: Friend request created successfully:', requestId);
      console.log('✅ V167.INVITE.FIX: Invite code stored:', inviteCode);
      console.log('🎉 V167.INVITE.FIX: The inviter will receive the request and can accept it!');

      // Note: Trust scores will be updated when the request is accepted
      // This happens automatically through the existing connection approval flow

      return { success: true, connectionId: requestId };

    } catch (error) {
      console.error('❌ V170.3: Exception creating friend request:', error);
      return { success: false, error: 'Friend request creation failed' };
    }
  }

  /**
   * 🔒 SECURITY: Create connection request with comprehensive validation
   * V170.3: Added cache check to prevent duplicate key errors
   */
  private async createConnectionRequest(senderId: string, recipientId: string, inviteCode: string): Promise<{ success: boolean; requestId?: string; declined?: boolean; declinedAt?: string; error?: string }> {
    try {
      const idempotencyKey = `create-request:${senderId}:${recipientId}`;
      const { data, error } = await supabase.rpc('create_connection_request_canonical', {
        p_sender_id: senderId,
        p_recipient_id: recipientId,
        p_invite_code: inviteCode,
        p_idempotency_key: idempotencyKey,
      });

      if (error) {
        return { success: false, error: error.message || 'Failed to create connection request' };
      }

      const row = Array.isArray(data) ? data[0] : data;
      if (!row?.success) {
        return {
          success: false,
          requestId: row?.request_id || undefined,
          error: row?.error || 'Failed to create connection request',
        };
      }

      return { success: true, requestId: row.request_id };

    } catch (error) {
      console.error('💥 V152.2: Unexpected error in createConnectionRequest:', error);
      return { success: false, error: 'Unexpected error creating request' };
    }
  }

  /**
   * V283.8: DEPRECATED - Use createConnectionRequest directly
   * This method is kept for backward compatibility but should not be used
   * @deprecated Use createConnectionRequest(senderId, recipientId, inviteCode) directly
   */
  async createMutualConnectionRequests(inviterId: string, inviteeId: string, inviteCode: string): Promise<{ success: boolean; error?: string }> {
    console.warn('⚠️ V283.8: createMutualConnectionRequests is DEPRECATED - use createConnectionRequest directly');
    console.warn('⚠️ V283.8: This method has confusing semantics - caller should use createConnectionRequest with explicit sender/recipient');

    // V283.8: For backward compatibility, assume invitee sends request to inviter
    return this.createConnectionRequest(inviteeId, inviterId, inviteCode)
      .then(result => ({ success: result.success, error: result.error }));
  }

  /**
   * Increment invitation usage counter
   * V283.7: Fixed "it.sql is not a function" error - use RPC instead
   */
  private async incrementInvitationUsage(invitationId: string): Promise<void> {
    try {
      console.log('🔧 V283.7: Incrementing invitation usage for:', invitationId);

      // V283.7: Use RPC function to increment atomically
      const { error } = await supabase.rpc('increment_invitation_usage', {
        invitation_id: invitationId
      });

      if (error) {
        console.error('⚠️ V283.7: Error incrementing invitation usage:', error);
        // Fallback: fetch current value and update manually
        const { data: invitation } = await supabase
          .from('general_invitations')
          .select('current_uses')
          .eq('id', invitationId)
          .single();

        if (invitation) {
          await supabase
            .from('general_invitations')
            .update({
              current_uses: (invitation.current_uses || 0) + 1,
              updated_at: new Date().toISOString()
            })
            .eq('id', invitationId);
          console.log('✅ V283.7: Invitation usage incremented via fallback');
        }
      } else {
        console.log('✅ V283.7: Invitation usage incremented successfully');
      }
    } catch (error) {
      console.error('⚠️ V283.7: Error updating invitation usage:', error);
      // Non-critical error, don't fail the whole process
    }
  }

  /**
   * Send notification to creator about new connection request
   */
  private async notifyCreatorOfConnectionRequest(creatorId: string, requesterId: string, inviteCode: string): Promise<void> {
    try {
      // Get requester profile for notification
      const requesterProfile = await getPublicUserV1ById(requesterId);

      if (requesterProfile) {
        // 🔔 REAL NOTIFICATION: Send actual browser notification
        await notificationService.sendConnectionRequestNotification({
          id: crypto.randomUUID(),
          requesterId,
          requesterName: requesterProfile.name || 'New BlissNet user',
          requesterPhoto: requesterProfile.photo_urls?.[0],
          inviteCode,
          timestamp: new Date()
        });

        console.log('🔔 NOTIFICATION: Connection request notification sent', {
          creatorId,
          requesterId,
          requesterName: requesterProfile.name,
          inviteCode
        });
      }

    } catch (error) {
      console.error('⚠️ Error sending notification:', error);
      // Non-critical error, don't fail the whole process
    }
  }

  /**
   * Send notification to creator about new direct connection
   */
  private async notifyCreatorOfNewConnection(creatorId: string, newUserId: string, inviteCode: string): Promise<void> {
    try {
      // Get new user profile for notification
      const newUserProfile = await getPublicUserV1ById(newUserId);

      if (newUserProfile) {
        // 🔔 REAL NOTIFICATION: Send actual browser notification about new connection
        await notificationService.sendNewConnectionNotification({
          id: crypto.randomUUID(),
          newUserId,
          newUserName: newUserProfile.name || 'New BlissNet user',
          newUserPhoto: newUserProfile.photo_urls?.[0],
          inviteCode,
          timestamp: new Date()
        });

        console.log('🔔 NOTIFICATION: New connection notification sent', {
          creatorId,
          newUserId,
          newUserName: newUserProfile.name,
          inviteCode
        });
      }

    } catch (error) {
      console.error('⚠️ Error sending new connection notification:', error);
      // Non-critical error, don't fail the whole process
    }
  }

  /**
   * V152.9: AUGMENT - Manual join fallback for connection requests
   */
  async getPendingConnectionRequestsManual(userId: string): Promise<ConnectionRequestNotification[]> {
    try {
      console.log('⚡ V152.9: Using manual join fallback for user:', userId);

      // V163.4: Connection requests now loaded via NetworkDataContext
      console.warn('⚠️ V163.4: getConnectionRequestsForUser() - Use NetworkDataContext for connection requests');
      const rawRequests = []; // Neutralized - use useConnectionRequests() hook
      const requestError = null;

      if (requestError) {
        console.error('❌ V152.9: Error fetching raw requests:', requestError);
        return [];
      }

      console.log('✅ V152.9: Fetched raw requests:', rawRequests?.length || 0);

      // Manually fetch sender data for each request
      const requestsWithSenders = await Promise.all(
        (rawRequests || []).map(async (request) => {
          console.log('⚡ V152.9: Fetching sender data for:', request.sender_id);

          const sender = await getPublicUserV1ById(request.sender_id);
          const senderError = sender ? null : new Error('sender_not_found');

          if (senderError) {
            console.error('❌ V152.9: Error fetching sender:', senderError);
            return {
              id: request.id,
              sender_id: request.sender_id,
              recipient_id: request.recipient_id,
              sender_name: 'New BlissNet user',
              sender_photo: undefined,
              invite_code: request.invite_code,
              status: request.status as 'pending' | 'final_approval_pending',
              created_at: request.created_at
            };
          }

          console.log('✅ V152.9: Sender fetched:', sender?.name);
          return {
            id: request.id,
            sender_id: request.sender_id,
            recipient_id: request.recipient_id,
            sender_name: sender?.name || 'New BlissNet user',
            sender_photo: sender?.photo_urls?.[0],
            invite_code: request.invite_code,
            status: request.status as 'pending' | 'final_approval_pending',
            created_at: request.created_at
          };
        })
      );

      console.log('🎉 V152.9: All requests loaded with sender data via manual join');
      return requestsWithSenders;

    } catch (error) {
      console.error('💥 V152.9: Error in manual join fallback:', error);
      return [];
    }
  }

  /**
   * Get pending connection requests for a user (for approval interface)
   * V160.4: Filter out requests from users who are already connected
   */
  async getPendingConnectionRequests(userId: string): Promise<ConnectionRequestNotification[]> {
    try {
      // V163.6: Use NetworkDataContext instead of coordinator
      console.warn('⚠️ V163.6: generalInvitationService - Use useConnectionRequests() hook from NetworkDataContext instead');

      const requests = []; // Neutralized - use context hook

      // V163.6: Components should use NetworkDataContext
      console.log('✅ V163.6: Connection requests managed by NetworkDataContext, requests:', requests?.length || 0);
      // console.log('🔍 V152.9: Raw requests data:', requests);

      if (requests && requests.length > 0) {
        // V159.2.3: Only log on errors, not every time
        // console.log('🔍 V152.9: First request sender data:', requests[0].sender);
        // console.log('🔍 V152.9: Sender photo_urls:', requests[0].sender?.photo_urls);

        // V152.9: Check if sender data is actually loaded
        if (!requests[0].sender || !requests[0].sender.name) {
          console.warn('⚠️ V152.9: Sender data is null with simplified join, using manual fallback');
          return await this.getPendingConnectionRequestsManual(userId);
        }

        // V160.4: Filter out requests from users who are already connected
        const filteredRequests = [];
        for (const req of requests) {
          const connectionCheck = await this.checkExistingConnectionComprehensive(userId, req.sender_id);
          if (!connectionCheck.exists) {
            filteredRequests.push(req);
          } else {
            // Filtered out - already connected
          }
        }

        // Filtered connection requests

        // Return filtered requests
        return filteredRequests?.map(req => {
          const mappedRequest = {
            id: req.id,
            sender_id: req.sender_id,
            recipient_id: req.recipient_id,
            sender_name: req.sender?.name || 'New BlissNet user',
            sender_photo: req.sender?.photo_urls?.[0],
            invite_code: req.invite_code,
            status: req.status as 'pending' | 'final_approval_pending',
            created_at: req.created_at
          };

          return mappedRequest;
        }) || [];
      }

      // V160.4: If no filtering was needed, map the original requests
      return requests?.map(req => {
        const mappedRequest = {
          id: req.id,
          sender_id: req.sender_id,
          recipient_id: req.recipient_id,
          sender_name: req.sender?.name || 'New BlissNet user',
          sender_photo: req.sender?.photo_urls?.[0],
          invite_code: req.invite_code,
          status: req.status as 'pending' | 'final_approval_pending',
          created_at: req.created_at
        };

        return mappedRequest;
      }) || [];

    } catch (error) {
      console.error('💥 Error in getPendingConnectionRequests:', error);
      return [];
    }
  }

  /**
   * 🔒 MUTUAL CONSENT: Step 1 - Approve connection request (does not create friendship yet)
   */
  async approveConnectionRequest(requestId: string, approverId: string): Promise<{ success: boolean; requiresMutualConsent?: boolean; requesterInfo?: any; error?: string }> {
    const mutationKey = `approve:${requestId}:${approverId}`;
    return this.withSingleFlight(mutationKey, async () => {
      try {
        console.log('[InviteFlow] approve mutation begin', { mutationKey, requestId, approverId });
        const { data, error } = await supabase.rpc('approve_connection_request_canonical', {
          p_request_id: requestId,
          p_approver_id: approverId,
          p_idempotency_key: mutationKey,
        });

        if (error) {
          console.error('[InviteFlow] approve mutation error', { mutationKey, error });
          return { success: false, error: 'Failed to approve connection request' };
        }

        const rpcRow = Array.isArray(data) ? data[0] : data;
        if (!rpcRow?.success) {
          console.warn('[InviteFlow] approve mutation failed', { mutationKey, rpcRow });
          return { success: false, error: rpcRow?.error || 'Failed to approve connection request' };
        }

        if (rpcRow.sender_id && rpcRow.recipient_id) {
          removeDeclinedRequest(rpcRow.sender_id, rpcRow.recipient_id);

          // Non-fatal notification restore: ensure requester/approver get friendship-created signal.
          this.notifyFriendshipCreated(rpcRow.sender_id, rpcRow.recipient_id).catch((notifyError) => {
            console.warn('[InviteFlow] approve notification failed (non-fatal)', { mutationKey, notifyError });
          });

          // Keep prior last-active behavior as fire-and-forget.
          import('../../utils/dbOperationWrapper').then(({ updateLastActiveNow }) => {
            updateLastActiveNow().catch(() => {
              // Non-fatal by design
            });
          });
        }

        console.log('[InviteFlow] approve mutation success', { mutationKey, decision: rpcRow.decision });
        return {
          success: true,
          requiresMutualConsent: false,
          requesterInfo: rpcRow.connection_id ? { connectionId: rpcRow.connection_id } : undefined,
        };
      } catch (error) {
        console.error('[InviteFlow] approve mutation terminal error', { mutationKey, error });
        return { success: false, error: 'Unexpected error occurred' };
      }
    });
  }

  /**
   * 🤝 MUTUAL CONSENT: Step 2 - Confirm friendship desire (invitation creator confirms they want to be friends)
   */
  async confirmMutualConsent(requestId: string, approverId: string, wantsFriendship: boolean): Promise<{ success: boolean; requiresFinalApproval?: boolean; error?: string }> {
    // Legacy 3-step path is intentionally quarantined.
    console.warn('⚠️ confirmMutualConsent is deprecated and not part of production canonical flow', {
      requestId,
      approverId,
      wantsFriendship,
    });
    return {
      success: false,
      error: 'Legacy mutual-consent path is disabled. Use canonical approve/reject RPC flow.',
    };
  }

  /**
   * 🎯 FINAL APPROVAL: Step 3 - Random User gives final approval for friendship
   */
  async giveFinalApproval(requestId: string, approverId: string, approvesFriendship: boolean): Promise<{ success: boolean; chatId?: string; error?: string }> {
    try {
      console.log('🎯 FINAL APPROVAL (canonical)', { requestId, approverId, approvesFriendship });

      if (!approvesFriendship) {
        const rejectResult = await this.rejectConnectionRequest(requestId, approverId);
        return { success: rejectResult.success, error: rejectResult.error };
      }

      const idempotencyKey = `approve:${requestId}:${approverId}`;
      const { data, error } = await supabase.rpc('approve_connection_request_canonical', {
        p_request_id: requestId,
        p_approver_id: approverId,
        p_idempotency_key: idempotencyKey,
      });

      if (error) {
        return { success: false, error: error.message || 'Failed to approve friendship' };
      }

      const row = Array.isArray(data) ? data[0] : data;
      if (!row?.success) {
        return { success: false, error: row?.error || 'Failed to approve friendship' };
      }

      if (row.sender_id && row.recipient_id) {
        removeDeclinedRequest(row.sender_id, row.recipient_id);
        try {
          await supabase.rpc('recompute_trust_for_users', { user_ids: [row.sender_id, row.recipient_id] });
        } catch (e) {
          console.warn('[TrustScore] recompute RPC failed', e);
        }
        await this.notifyFriendshipCreated(row.sender_id, row.recipient_id);
      }

      return { success: true, chatId: row.connection_id };

    } catch (error) {
      console.error('💥 Error in final approval:', error);
      return { success: false, error: 'Unexpected error occurred' };
    }
  }

  /**
   * 🔔 REAL-TIME: Send connection approval notification to requester
   */
  async sendConnectionApprovalNotification(requesterId: string, approverName: string, approverPhoto?: string): Promise<void> {
    try {
      // Sending connection approval notification

      // Send real-time broadcast notification to the requester
      const { error } = await supabase
        .channel('connection-approvals')
        .send({
          type: 'broadcast',
          event: 'connection_approved',
          payload: {
            requesterId,
            approverName,
            approverPhoto,
            timestamp: new Date().toISOString(),
            message: `${approverName} accepted your connection request`
          }
        });

      if (error) {
        console.error('❌ Error sending connection approval broadcast:', error);
      } else {
        console.log('✅ Connection approval notification sent via broadcast');
      }

    } catch (error) {
      console.error('💥 Error in sendConnectionApprovalNotification:', error);
    }
  }

  /**
   * 🔔 MUTUAL CONSENT: Notify user about final approval request
   */
  private async notifyFinalApprovalRequest(userId: string, inviterName: string, inviteCode: string): Promise<void> {
    try {
      console.log('🔔 MUTUAL CONSENT: Sending final approval notification', { userId, inviterName });

      // Send real-time broadcast notification
      const { error } = await supabase
        .channel('final-approval-notifications')
        .send({
          type: 'broadcast',
          event: 'final_approval_request',
          payload: {
            userId,
            inviterName,
            inviteCode,
            message: `${inviterName} wants to be friends with you`,
            timestamp: new Date().toISOString()
          }
        });

      if (error) {
        console.error('❌ Error sending final approval notification:', error);
      } else {
        console.log('✅ Final approval notification sent via broadcast');
      }

    } catch (error) {
      console.error('💥 Error in notifyFinalApprovalRequest:', error);
    }
  }

  /**
   * 🎉 MUTUAL CONSENT: Notify both parties about successful friendship creation
   */
  private async notifyFriendshipCreated(userId1: string, userId2: string): Promise<void> {
    try {
      console.log('🎉 MUTUAL CONSENT: Sending friendship created notifications', { userId1, userId2 });

      // Get both user profiles for notifications
      const users = await getPublicUserV1ByIds([userId1, userId2]);

      if (!users || users.length !== 2) {
        console.error('❌ Could not fetch user profiles for friendship notifications');
        return;
      }

      const user1 = users.find(u => u.id === userId1);
      const user2 = users.find(u => u.id === userId2);

      // Send notifications to both users
      const notifications = [
        {
          userId: userId1,
          friendName: user2?.name || 'Someone',
          friendPhoto: user2?.photo_urls?.[0]
        },
        {
          userId: userId2,
          friendName: user1?.name || 'Someone',
          friendPhoto: user1?.photo_urls?.[0]
        }
      ];

      for (const notification of notifications) {
        const { error } = await supabase
          .channel('friendship-notifications')
          .send({
            type: 'broadcast',
            event: 'friendship_created',
            payload: {
              userId: notification.userId,
              friendName: notification.friendName,
              friendPhoto: notification.friendPhoto,
              message: `You and ${notification.friendName} are now friends!`,
              timestamp: new Date().toISOString()
            }
          });

        if (error) {
          console.error('❌ Error sending friendship notification:', error);
        }
      }

      console.log('✅ Friendship created notifications sent to both parties');

    } catch (error) {
      console.error('💥 Error in notifyFriendshipCreated:', error);
    }
  }

  /**
   * 🔒 SECURE: Decline connection request (recipient control)
   */
  async rejectConnectionRequest(requestId: string, rejecterId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const idempotencyKey = `reject:${requestId}:${rejecterId}`;
      const { data, error } = await supabase.rpc('reject_connection_request_canonical', {
        p_request_id: requestId,
        p_rejecter_id: rejecterId,
        p_idempotency_key: idempotencyKey,
      });

      if (error) {
        return { success: false, error: error.message || 'Failed to decline request' };
      }

      const row = Array.isArray(data) ? data[0] : data;
      if (!row?.success) {
        return { success: false, error: row?.error || 'Failed to decline request' };
      }

      if (row.sender_id && row.recipient_id) {
        addDeclinedRequest(row.sender_id, row.recipient_id, undefined, new Date().toISOString());
      }
      return { success: true };

    } catch (error) {
      console.error('💥 V171.DECLINE: Error declining connection request:', error);
      return { success: false, error: 'Unexpected error occurred' };
    }
  }

  /**
   * 🔒 SECURITY: Audit log for tracking invitation activities
   */
  private async logSecurityEvent(event: string, details: any): Promise<void> {
    try {
      const logEntry = {
        timestamp: new Date().toISOString(),
        event,
        details,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'Unknown',
        url: typeof window !== 'undefined' ? window.location.href : 'Unknown'
      };

      console.log('🔒 SECURITY AUDIT:', logEntry);

      // In production, this would be sent to a security monitoring service
      // For now, we'll use console logging and could extend to send to Supabase

    } catch (error) {
      console.error('⚠️ Failed to log security event:', error);
      // Don't fail the main operation if logging fails
    }
  }

  /**
   * 🔒 SECURITY: Validate invite code format to prevent injection attacks
   */
  private validateInviteCodeFormat(inviteCode: string): boolean {
    // Check for basic format validation
    if (!inviteCode || typeof inviteCode !== 'string') {
      return false;
    }

    // Check length (should be reasonable)
    if (inviteCode.length < 3 || inviteCode.length > 50) {
      return false;
    }

    // Check for suspicious characters that might indicate injection attempts
    const suspiciousPatterns = [
      /[<>]/,           // HTML tags
      /javascript:/i,   // JavaScript protocol
      /data:/i,         // Data protocol
      /vbscript:/i,     // VBScript protocol
      /on\w+=/i,        // Event handlers
      /script/i,        // Script tags
      /['"]/,           // Quotes that might break out of attributes
      /[{}]/,           // Curly braces
      /\\/,             // Backslashes
      /;/,              // Semicolons
      /--/,             // SQL comment
      /\/\*/,           // Multi-line comment start
      /\*\//            // Multi-line comment end
    ];

    for (const pattern of suspiciousPatterns) {
      if (pattern.test(inviteCode)) {
        console.log('❌ SECURITY: Suspicious invite code pattern detected:', pattern);
        return false;
      }
    }

    return true;
  }

  /**
   * 🔒 SECURITY: Enhanced process method with additional validation
   * Handles both general and legacy invites
   * V167.INVITE: General invites now create DIRECT ACCEPTED connections (auto-approved)
   */
  async processGeneralInvitationSecure(inviteCode: string, userId: string): Promise<GeneralInviteProcessResult> {
    try {
      console.log('🔒 V167.INVITE: Processing general invitation (SECURE):', { inviteCode, userId });

      // Input validation
      if (!inviteCode || !userId) {
        return {
          success: false,
          requiresApproval: false,
          alreadyConnected: false,
          error: 'Invalid input: invite code and user ID are required'
        };
      }

      // Security validation before processing
      if (!this.validateInviteCodeFormat(inviteCode)) {
        await this.logSecurityEvent('INVALID_INVITE_CODE_FORMAT', { inviteCode, userId });
        return { success: false, requiresApproval: false, alreadyConnected: false, error: 'Invalid invitation format' };
      }

      // Log the invitation processing attempt
      await this.logSecurityEvent('GENERAL_INVITE_PROCESS_START', { inviteCode, userId });

      // Get the invite (handles both general and legacy)
      const invite = await this.getInviteByCode(inviteCode);

      if (!invite) {
        console.log('❌ V167.INVITE: Invite not found in any system');
        return {
          success: false,
          requiresApproval: false,
          alreadyConnected: false,
          error: 'Invalid or expired invitation'
        };
      }

      // Security validations
      if (invite.creator_id === userId) {
        return {
          success: false,
          requiresApproval: false,
          alreadyConnected: false,
          error: 'Cannot accept your own invite'
        };
      }

      // Check if connection already exists
      const connectionExists = await this.checkExistingConnectionComprehensive(invite.creator_id, userId);
      if (connectionExists.exists) {
        console.log('✅ V167.INVITE: Connection already exists');
        return {
          success: true,
          requiresApproval: false,
          alreadyConnected: true,
          creatorProfile: invite.creator
        };
      }

      // Handle legacy invites differently (still use approval flow)
      if (invite.isLegacy) {
        console.log('🔄 V167.INVITE: Processing legacy invite with approval flow');
        const result = await this.processLegacyInviteSecure(invite, userId);
        return {
          success: result.success,
          requiresApproval: true,
          alreadyConnected: false,
          error: result.error,
          creatorProfile: invite.creator
        };
      }

      // V167.INVITE.FIX: Handle general invites with FRIEND REQUEST (respects V162 architecture)
      console.log('🚀 V167.INVITE.FIX: Creating friend request for general invite');
      console.log('🚀 V167.INVITE.FIX: Passing invite code:', inviteCode);
      const friendRequestResult = await this.createDirectConnection(userId, invite.creator_id, inviteCode);

      if (!friendRequestResult.success) {
        console.error('❌ V167.INVITE.FIX: Failed to create friend request:', friendRequestResult.error);
        return {
          success: false,
          requiresApproval: false,
          alreadyConnected: false,
          error: friendRequestResult.error || 'Failed to send friend request',
          creatorProfile: invite.creator
        };
      }

      console.log('✅ V167.INVITE.FIX: Friend request sent successfully!');
      console.log('🎉 V167.INVITE.FIX: The inviter will receive the request and can accept it!');
      console.log('🎉 V167.INVITE.FIX: After acceptance, both users will get +10 trust score!');

      // Increment invite usage count
      try {
        await supabase
          .from('general_invitations')
          .update({
            current_uses: (invite.current_uses || 0) + 1,
            updated_at: new Date().toISOString()
          })
          .eq('id', invite.id);
      } catch (usageError) {
        console.warn('⚠️ V167.INVITE: Failed to increment usage count:', usageError);
        // Don't fail the whole operation
      }

      return {
        success: true,
        requiresApproval: true, // V167.INVITE.FIX: Friend request sent - requires inviter approval
        alreadyConnected: false,
        connectionRequestId: friendRequestResult.connectionId,
        creatorProfile: invite.creator
      };

    } catch (error) {
      console.error('💥 V167.INVITE: Failed to process general invitation (SECURE):', error);
      return {
        success: false,
        requiresApproval: false,
        alreadyConnected: false,
        error: 'Failed to process invitation'
      };
    }
  }

  /**
   * 🚨 FIXED: Process legacy invite securely with per-user duplicate prevention
   * Enables unlimited users to use same general invitation link
   */
  async processLegacyInviteSecure(invite: any, userId: string) {
    try {
      console.log('🔄 Processing legacy invite securely with per-user duplicate prevention');

      // V163.4: Connection requests now loaded via NetworkDataContext
      console.warn('⚠️ V163.4: processLegacyInviteSecure() - Use NetworkDataContext for connection requests');
      const userSpecificRequest = null; // Neutralized - use useConnectionRequests() hook

      if (userSpecificRequest) {
        console.log('🔍 LEGACY: Found existing request for this user', {
          requestId: userSpecificRequest.id,
          status: userSpecificRequest.status,
          userId,
          inviteCode: invite.invite_code
        });

        if (userSpecificRequest.status === 'accepted') {
          return {
            success: false,
            error: 'You have already used this invitation'
          };
        } else if (userSpecificRequest.status === 'declined') {
          return {
            success: false,
            error: 'You have already declined this invitation'
          };
        } else if (userSpecificRequest.status === 'PENDING') {
          return {
            success: false,
            error: 'You already have a pending request for this invitation'
          };
        }
      }

      // 🚀 ENABLE VIRAL GROWTH: Create NEW connection request for this user
      // Multiple users can use the same invitation - each gets their own request
      console.log('✅ LEGACY: Creating new connection request for user', {
        userId,
        creatorId: invite.creator_id,
        inviteCode: invite.invite_code
      });

      const connectionRequest = await this.createConnectionRequest(userId, invite.creator_id, invite.invite_code);

      if (!connectionRequest.success) {
        return {
          success: false,
          error: 'Failed to create connection request'
        };
      }

      // Notify the creator
      await this.notifyCreatorOfConnectionRequest(invite.creator_id, userId, invite.invite_code);

      console.log('✅ Legacy invite processed successfully');
      return {
        success: true,
        message: 'Connection request sent! The other person needs to approve.',
        isLegacy: true
      };
    } catch (error) {
      console.error('💥 Failed to process legacy invite:', error);
      return {
        success: false,
        error: 'Failed to process legacy invitation'
      };
    }
  }

  /**
   * Process new general invite securely
   */
  async processNewInviteSecure(invite: any, userId: string) {
    try {
      console.log('🆕 Processing new general invite securely');

      // Check if connection request already exists
      const requestExists = await this.checkExistingConnectionRequest(userId, invite.creator_id);
      if (requestExists.exists) {
        console.log('✅ Connection request already exists, treating as success');
        return {
          success: true,
          connectionRequest: { requestId: requestExists.requestId },
          message: 'Connection request already sent and pending approval',
          alreadyRequested: true
        };
      }

      // Check usage limits
      if (invite.max_uses && invite.current_uses >= invite.max_uses) {
        return {
          success: false,
          error: 'Invitation has reached maximum uses'
        };
      }

      // Check expiry
      if (invite.expires_at && new Date(invite.expires_at) < new Date()) {
        return {
          success: false,
          error: 'Invitation has expired'
        };
      }

      // Create CONNECTION REQUEST
      const connectionRequest = await this.createConnectionRequest(userId, invite.creator_id, invite.invite_code);

      if (!connectionRequest.success) {
        return {
          success: false,
          error: 'Failed to create connection request'
        };
      }

      // Update invitation usage count
      await supabase
        .from('general_invitations')
        .update({
          current_uses: invite.current_uses + 1,
          updated_at: new Date().toISOString()
        })
        .eq('id', invite.id);

      // Notify the invite creator
      await this.notifyCreatorOfConnectionRequest(invite.creator_id, userId, invite.invite_code);

      console.log('✅ New general invite processed successfully');
      return {
        success: true,
        connectionRequest,
        message: 'Connection request sent! The other person needs to approve.'
      };
    } catch (error) {
      console.error('💥 Failed to process new general invite:', error);
      return {
        success: false,
        error: 'Failed to process new invitation'
      };
    }
  }

  /**
   * 🔧 OPTIMIZED: Create general invitation - ensures exactly ONE active invitation per user
   * V177: Each user should have only 1 invitation link - if 0 make one - no more
   */
  async createGeneralInvitation(creatorId: string, customMessage?: string, options?: {
    maxUses?: number;
    expiresInDays?: number;
  }): Promise<{ success: boolean; inviteCode?: string; inviteLink?: string; error?: string }> {
    try {
      // V177: Check if user already has active invitation(s)
      const { data: existingInvites, error: checkError } = await supabase
        .from('general_invitations')
        .select('id, invite_code, current_uses, max_uses, created_at')
        .eq('creator_id', creatorId)
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (checkError) {
        console.error('❌ V177: Error checking existing invitations:', checkError);
        // Continue to create new invitation
      }

      // V177: If user has exactly one active invitation, return it (don't create duplicate)
      if (existingInvites && existingInvites.length === 1) {
        const existingInvite = existingInvites[0];
        console.log('✅ V177: User already has one active invitation, reusing:', existingInvite.invite_code);
        return {
          success: true,
          inviteCode: existingInvite.invite_code,
          inviteLink: `https://blissnet.app/invite/${existingInvite.invite_code}`
        };
      }

      // V177: If user has multiple active invitations, deactivate all except the most recent
      if (existingInvites && existingInvites.length > 1) {
        console.warn(`⚠️ V177: User has ${existingInvites.length} active invitations, deactivating old ones`);
        const mostRecent = existingInvites[0]; // Already sorted by created_at desc
        const oldInviteIds = existingInvites.slice(1).map(inv => inv.id);

        if (oldInviteIds.length > 0) {
          const { error: deactivateError } = await supabase
            .from('general_invitations')
            .update({ is_active: false, updated_at: new Date().toISOString() })
            .in('id', oldInviteIds);

          if (deactivateError) {
            console.error('❌ V177: Error deactivating old invitations:', deactivateError);
          } else {
            console.log(`✅ V177: Deactivated ${oldInviteIds.length} old invitations, keeping:`, mostRecent.invite_code);
          }
        }

        // Return the most recent active invitation
        return {
          success: true,
          inviteCode: mostRecent.invite_code,
          inviteLink: `https://blissnet.app/invite/${mostRecent.invite_code}`
        };
      }

      // V177: If user has 0 active invitations, create a new one
      console.log('✅ V177: User has no active invitation, creating new one');

      // Generate optimized invite code (shorter, more readable)
      const timestamp = Date.now().toString(36);
      const cleanUserId = creatorId.replace(/-/g, '').substring(0, 6); // Shorter
      const randomSuffix = Math.random().toString(36).substring(2, 4); // Shorter
      const inviteCode = `${cleanUserId}-${timestamp}-${randomSuffix}`;

      // Smart expiration (default 1 year if not specified)
      const expiresAt = options?.expiresInDays
        ? new Date(Date.now() + options.expiresInDays * 24 * 60 * 60 * 1000).toISOString()
        : new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(); // 1 year default

      // V177: Create general invitation record with template_key (no more message field)
      const { data: invitation, error: inviteError } = await supabase
        .from('general_invitations')
        .insert({
          creator_id: creatorId,
          invite_code: inviteCode,
          template_key: 'whatsapp_default', // V177: Use template instead of message
          max_uses: options?.maxUses || 10000, // Default 10,000 uses
          current_uses: 0,
          expires_at: expiresAt,
          is_active: true,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select('*')
        .single();

      if (inviteError) {
        console.error('❌ Error creating general invitation:', inviteError);
        return { success: false, error: 'Failed to create invitation' };
      }

      // Generate invite link
      const inviteLink = `https://blissnet.app/invite/${inviteCode}`;

      console.log('✅ General invitation created successfully', {
        inviteCode,
        creatorId,
        inviteLink
      });

      return {
        success: true,
        inviteCode,
        inviteLink
      };

    } catch (error) {
      console.error('💥 Error in createGeneralInvitation:', error);
      return { success: false, error: 'Unexpected error occurred' };
    }
  }

  /**
   * 🔧 DATA CONSISTENCY: Migrate existing connection_request to general_invitation
   */
  async migrateConnectionRequestToGeneral(inviteCode: string): Promise<{ success: boolean; error?: string }> {
    try {
      // V163.4: Connection requests now loaded via NetworkDataContext
      console.warn('⚠️ V163.4: migrateConnectionRequestToGeneral() - Use NetworkDataContext for connection requests');
      const connectionRequest = null; // Neutralized - use useConnectionRequests() hook
      const requestError = null;

      if (requestError || !connectionRequest) {
        return { success: false, error: 'Connection request not found' };
      }

      // Check if general_invitation already exists
      const { data: existingInvite, error: existingError } = await supabase
        .from('general_invitations')
        .select('id')
        .eq('invite_code', inviteCode)
        .maybeSingle();

      if (existingError) {
        return { success: false, error: 'Error checking existing invitation' };
      }

      if (existingInvite) {
        return { success: true }; // Already exists, no need to migrate
      }

      // V177: Create the missing general_invitation record with template_key
      const { error: insertError } = await supabase
        .from('general_invitations')
        .insert({
          creator_id: connectionRequest.sender_id,
          invite_code: inviteCode,
          template_key: 'whatsapp_default', // V177: Use template instead of message
          max_uses: null,
          current_uses: 1, // Already used once
          expires_at: null,
          is_active: true,
          created_at: connectionRequest.created_at,
          updated_at: new Date().toISOString()
        });

      if (insertError) {
        console.error('❌ Error migrating to general invitation:', insertError);
        return { success: false, error: 'Failed to migrate invitation' };
      }

      console.log('✅ Successfully migrated connection_request to general_invitation', {
        inviteCode,
        creatorId: connectionRequest.sender_id
      });

      return { success: true };

    } catch (error) {
      console.error('💥 Error in migrateConnectionRequestToGeneral:', error);
      return { success: false, error: 'Unexpected error occurred' };
    }
  }

  /**
   * 🧹 EFFICIENCY: Clean up expired and overused invitations
   */
  async cleanupExpiredInvitations(): Promise<{ cleaned: number; error?: string }> {
    try {
      const now = new Date().toISOString();

      // Deactivate expired invitations
      const { data: expiredResult, error: expiredError } = await supabase
        .from('general_invitations')
        .update({ is_active: false, updated_at: now })
        .lt('expires_at', now)
        .eq('is_active', true)
        .select('id');

      if (expiredError) {
        console.error('❌ Error cleaning expired invitations:', expiredError);
        return { cleaned: 0, error: expiredError.message };
      }

      // Deactivate overused invitations
      const { data: overusedResult, error: overusedError } = await supabase
        .from('general_invitations')
        .update({ is_active: false, updated_at: now })
        .filter('current_uses', 'gte', supabase.sql`max_uses`)
        .eq('is_active', true)
        .not('max_uses', 'is', null)
        .select('id');

      if (overusedError) {
        console.error('❌ Error cleaning overused invitations:', overusedError);
        return { cleaned: 0, error: overusedError.message };
      }

      const totalCleaned = (expiredResult?.length || 0) + (overusedResult?.length || 0);
      console.log(`🧹 V151.19: Cleaned ${totalCleaned} expired/overused invitations`);

      return { cleaned: totalCleaned };
    } catch (error) {
      console.error('💥 Error in cleanupExpiredInvitations:', error);
      return { cleaned: 0, error: 'Cleanup failed' };
    }
  }

  /**
   * 📊 ANALYTICS: Get invitation statistics for a user
   */
  async getInvitationStats(creatorId: string): Promise<{
    totalInvitations: number;
    activeInvitations: number;
    totalUses: number;
    successfulConnections: number;
  }> {
    try {
      // Get invitation stats
      const { data: inviteStats, error: inviteError } = await supabase
        .from('general_invitations')
        .select('current_uses, is_active')
        .eq('creator_id', creatorId);

      if (inviteError) {
        console.error('❌ Error fetching invitation stats:', inviteError);
        return { totalInvitations: 0, activeInvitations: 0, totalUses: 0, successfulConnections: 0 };
      }

      // V163.4: Connection requests now loaded via NetworkDataContext
      console.warn('⚠️ V163.4: getInvitationStats() - Use NetworkDataContext for connection requests');
      const connectionStats = []; // Neutralized - use useConnectionRequests() hook
      const connectionError = null;

      const totalInvitations = inviteStats?.length || 0;
      const activeInvitations = inviteStats?.filter(i => i.is_active).length || 0;
      const totalUses = inviteStats?.reduce((sum, i) => sum + (i.current_uses || 0), 0) || 0;
      const successfulConnections = connectionStats?.length || 0;

      return {
        totalInvitations,
        activeInvitations,
        totalUses,
        successfulConnections
      };
    } catch (error) {
      console.error('💥 Error in getInvitationStats:', error);
      return { totalInvitations: 0, activeInvitations: 0, totalUses: 0, successfulConnections: 0 };
    }
  }

  /**
   * V177: Get invitation share message with template from DB
   *
   * This function:
   * 1. Fetches the general_invitation by invite_code
   * 2. Joins with invitation_templates to get the message body
   * 3. Renders the template with the invite link
   * 4. Falls back to code constant if DB template unavailable
   *
   * @param inviteCode - The unique invite code
   * @returns Fully rendered invitation message ready to share
   */
  private async fetchActiveInviteTemplateBody(templateKey: string): Promise<string | null> {
    const { data: template, error: templateError } = await baseSupabase
      .from('invitation_templates')
      .select('body')
      .eq('key', templateKey)
      .eq('is_active', true)
      .maybeSingle();

    if (templateError) {
      console.error('❌ V177: Error fetching template:', templateError);
      return null;
    }

    return template?.body ?? null;
  }

  async getInvitationShareMessage(
    inviteCode: string,
    languageCode?: LanguageCode | null
  ): Promise<string> {
    try {
      console.log('🎨 V177: Getting invitation share message for:', inviteCode);

      // Step 1: Verify invitation exists (invite code generation unchanged)
      const { data: invitation, error: inviteError } = await baseSupabase
        .from('general_invitations')
        .select('invite_code')
        .eq('invite_code', inviteCode)
        .eq('is_active', true)
        .maybeSingle();

      if (inviteError) {
        console.error('❌ V177: Error fetching invitation:', inviteError);
        const inviteLink = buildInviteLink(inviteCode);
        return renderInviteMessage({ inviteLink, templateBody: null });
      }

      if (!invitation) {
        console.warn('⚠️ V177: Invitation not found:', inviteCode);
        const inviteLink = buildInviteLink(inviteCode);
        return renderInviteMessage({ inviteLink, templateBody: null });
      }

      const inviteLink = buildInviteLink(invitation.invite_code);

      // Step 2: Select template by current app language (not stored template_key)
      const lang =
        languageCode ?? normalizeLanguageCode(i18n.language) ?? 'en';
      const resolvedKey = resolveInviteTemplateKey(lang);

      let templateBody = await this.fetchActiveInviteTemplateBody(resolvedKey);
      if (!templateBody && resolvedKey !== ENGLISH_INVITE_TEMPLATE_KEY) {
        templateBody = await this.fetchActiveInviteTemplateBody(
          ENGLISH_INVITE_TEMPLATE_KEY
        );
      }

      console.log('✅ V177: Template fetched', {
        hasTemplate: !!templateBody,
        templateKey: resolvedKey,
        language: lang,
      });

      return renderInviteMessage({ inviteLink, templateBody });
    } catch (error) {
      console.error('💥 V177: Exception in getInvitationShareMessage:', error);
      const inviteLink = buildInviteLink(inviteCode);
      return renderInviteMessage({ inviteLink, templateBody: null });
    }
  }

  /**
   * V177: Get invitation share message by user ID
   *
   * Convenience method that fetches the user's active invitation
   * and returns the share message
   *
   * @param userId - The user's ID
   * @returns Fully rendered invitation message or null if no active invitation
   */
  async getInvitationShareMessageByUserId(
    userId: string,
    languageCode?: LanguageCode | null
  ): Promise<string | null> {
    try {
      console.log('🎨 V177: Getting invitation share message for user:', userId);

      // Fetch user's active invitation (handle multiple invitations)
      // Use limit(1) and order by most recent to get latest active invitation
      const { data: invitations, error } = await supabase
        .from('general_invitations')
        .select('invite_code')
        .eq('creator_id', userId)
        .eq('is_active', true)
        .order('created_at', { ascending: false })
        .limit(1);

      // Get first invitation from array (or null if empty)
      const invitation = Array.isArray(invitations) && invitations.length > 0 ? invitations[0] : null;

      if (error || !invitation) {
        console.warn('⚠️ V177: No active invitation found for user:', userId);
        return null;
      }

      // Get the share message
      return await this.getInvitationShareMessage(
        invitation.invite_code,
        languageCode
      );

    } catch (error) {
      console.error('💥 V177: Exception in getInvitationShareMessageByUserId:', error);
      return null;
    }
  }
}

export const generalInvitationService = new GeneralInvitationService();

